import { useCallback, useEffect, useLayoutEffect, useState } from 'react'

import { PanInfo } from 'framer-motion'

const DRAG_CONFIRM_THRESHOLD_PX = 80

export const dragCarouselTransition = { type: 'spring' as const, stiffness: 500, damping: 32 }

export const useDragCarousel = (itemCount: number, resetKey?: unknown) => {
  const [activeItemIndex, setActiveItemIndex] = useState(0)
  const [trackViewportWidth, setTrackViewportWidth] = useState(0)
  const [trackViewportElement, setTrackViewportElement] = useState<HTMLDivElement | null>(null)

  const trackViewportRef = useCallback((node: HTMLDivElement | null) => {
    setTrackViewportElement(node)
  }, [])

  useEffect(() => {
    setActiveItemIndex(0)
  }, [resetKey])

  useLayoutEffect(() => {
    if (!trackViewportElement) return

    const updateWidth = () => setTrackViewportWidth(trackViewportElement.clientWidth)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(trackViewportElement)

    return () => observer.disconnect()
  }, [trackViewportElement])

  const activeIndex = Math.min(activeItemIndex, Math.max(itemCount - 1, 0))

  const goToPrev = () => {
    setActiveItemIndex((prev) => Math.max(prev - 1, 0))
  }

  const goToNext = () => {
    setActiveItemIndex((prev) => Math.min(prev + 1, itemCount - 1))
  }

  const goToIndex = (index: number) => {
    setActiveItemIndex(index)
  }

  const handleTrackDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.x <= -DRAG_CONFIRM_THRESHOLD_PX && activeIndex < itemCount - 1) {
      goToNext()
    } else if (info.offset.x >= DRAG_CONFIRM_THRESHOLD_PX && activeIndex > 0) {
      goToPrev()
    }
  }

  return {
    activeIndex,
    trackViewportRef,
    trackViewportWidth,
    goToPrev,
    goToNext,
    goToIndex,
    handleTrackDragEnd,
  }
}
